import { float, stripHTML } from './index';

/* Format Mortgage Term */
export const formatMortgageTerm = (range, subType) => `${stripHTML(range)} - ${subType}`;

/* Calculate Total Mortgage Cost */
export const calculateMortgageCost = (askingPrice, downPaymentPercent) => {
    const askingPriceFloat = float(askingPrice);
    const downPaymentFloat = float(downPaymentPercent);

    const downPaymentAmount = askingPriceFloat * downPaymentFloat;
    const mortgageInsurance = calculateMortgageInsurance(askingPriceFloat, downPaymentFloat);

    return askingPriceFloat - downPaymentAmount + mortgageInsurance;
};

const calculateInsuranceRate = float => {
    // CMHC insurance rates
    const rates = {
        '0.05': 0.040,
        '0.10': 0.031,
        '0.15': 0.028
    };

    switch (true) {
        case (float < 0.05):
            return rates['0.05'];
        case (float < 0.1):
            return rates['0.05'];
        case (float < 0.15):
            return rates['0.10'];
        case (float < 0.2):
            return rates['0.15'];
        case (float >= 0.2):
            return 0;
        default:
            return rates['0.05'];
    }
};

/* Calculate Total Mortgage Cost */
export const calculateMortgageInsurance = (askingPrice, downPaymentPercent) => {
    const askingPriceFloat = float(askingPrice);
    const downPaymentFloat = float(downPaymentPercent);

    const downPaymentAmount = askingPriceFloat * downPaymentFloat;
    const insuranceRate = calculateInsuranceRate(downPaymentFloat);

    return (askingPriceFloat - downPaymentAmount) * insuranceRate;
};

/* Calculate Mortgage Payment */
export const calculateMortgagePayment = (askingPrice, downPaymentPercent, amortizationPeriod, paymentFrequencyNum, mortgageRate, paymentFrequency) => {
    const askingPriceFloat = float(askingPrice);
    const downPaymentFloat = float(downPaymentPercent);
    const amortizationPeriodFloat = float(amortizationPeriod);
    const paymentFrequencyFloat = (paymentFrequency === 'Bi-Weekly (Accelerated)' || paymentFrequency === 'Weekly (Accelerated)') ? 12 : float(paymentFrequencyNum);
    const mortgageRateFloat = float(mortgageRate);
    const totalPayments = amortizationPeriodFloat * paymentFrequencyFloat;

    const mortgageRatePercent = float((mortgageRateFloat / 100).toFixed(4));
    const effectiveAnnualRate = (((Math.pow((1 + (mortgageRatePercent / 2)), (2 / paymentFrequencyFloat))) - 1) * paymentFrequencyFloat);
    const effectiveRate = effectiveAnnualRate / paymentFrequencyFloat;

    const mortgageCost = calculateMortgageCost(askingPriceFloat, downPaymentFloat);

    return PMT(paymentFrequency, effectiveRate, totalPayments, mortgageCost);
};

/* Calculate max mortgage principal from affordable payment (reverse PMT / PV) */
export const calculateMaxMortgage = (maxPaymentMonthly, amortizationPeriod, mortgageRate) => {
    const amortizationPeriodFloat = float(amortizationPeriod);
    const mortgageRateFloat = float(mortgageRate);

    if (maxPaymentMonthly <= 0 || amortizationPeriodFloat <= 0 || mortgageRateFloat <= 0) {
        return maxPaymentMonthly > 0 ? maxPaymentMonthly * amortizationPeriodFloat * 12 : 0;
    }

    const mortgageRatePercent = float((mortgageRateFloat / 100).toFixed(4));
    // Effective monthly rate (Canadian semi-annual compounding)
    const effectiveMonthlyRate = Math.pow(1 + mortgageRatePercent / 2, 2 / 12) - 1;
    const totalPayments = amortizationPeriodFloat * 12;

    // PV = PMT * [(1 - (1+r)^-n) / r]
    const pvFactor = (1 - Math.pow(1 + effectiveMonthlyRate, -totalPayments)) / effectiveMonthlyRate;

    return maxPaymentMonthly * pvFactor;
};

/* Calculate payment for a given mortgage principal and frequency */
export const calculatePaymentFromPrincipal = (mortgagePrincipal, amortizationPeriod, paymentFrequencyNum, mortgageRate, paymentFrequency) => {
    const principal = float(mortgagePrincipal);
    const amortizationPeriodFloat = float(amortizationPeriod);
    const paymentFrequencyFloat = (paymentFrequency === 'Bi-Weekly (Accelerated)' || paymentFrequency === 'Weekly (Accelerated)') ? 12 : float(paymentFrequencyNum);
    const mortgageRateFloat = float(mortgageRate);

    if (principal <= 0 || amortizationPeriodFloat <= 0 || paymentFrequencyFloat <= 0) {
        return 0;
    }

    const totalPayments = amortizationPeriodFloat * paymentFrequencyFloat;
    const mortgageRatePercent = float((mortgageRateFloat / 100).toFixed(4));

    if (mortgageRatePercent <= 0) {
        return principal / totalPayments;
    }

    const effectiveAnnualRate = (Math.pow(1 + mortgageRatePercent / 2, 2 / paymentFrequencyFloat) - 1) * paymentFrequencyFloat;
    const effectiveRate = effectiveAnnualRate / paymentFrequencyFloat;

    return PMT(paymentFrequency, effectiveRate, totalPayments, principal);
};

/* PMT Excel function to calculate Mortgage Payment Amount */
export const PMT = (plan, ir, np, pv, fv, type) => {
    /*
     * ir   - interest rate per month
     * np   - number of periods (months)
     * pv   - present value
     * fv   - future value
     * type - when the payments are due:
     *        0: end of the period, e.g. end of month (default)
     *        1: beginning of period
     */
    let pmt, pvif;

    fv || (fv = 0);
    type || (type = 0);

    const rate = float(ir);
    const months = float(np);
    const mortgageValue = float(pv);
    const futureMortgageValue = float(fv);

    if (rate === 0) {
        return (mortgageValue + futureMortgageValue) / months;
    }

    pvif = Math.pow(1 + rate, months);
    pmt = rate * mortgageValue * (pvif + futureMortgageValue) / (pvif - 1);

    if (type === 1) {
        pmt /= (1 + rate);
    }
    if (plan === 'Bi-Weekly (Accelerated)') {
        return pmt / 2;
    }
    if (plan === 'Weekly (Accelerated)') {
        return pmt / 4;
    }

    return pmt;
};
